import { z } from "zod";

import {
  DocumentAttachment,
  ImageAttachment,
  LinkAttachment,
} from "../attachments/attachments.types.ts";
import { UUID } from "../generic/uuid.ts";

import { AgentStepPayload } from "./agentStep.ts";
import {
  RequestMessage,
  ResponseMessage,
  ResponseMessageContentTypeEnum,
  ResponseMessageStatusEnum,
} from "./messages.ts";
import { Target } from "./targets.ts";

// ================================================
// CHAT MESSAGE ATTACHMENTS
// ================================================

export const ChatMessageDocumentAttachment = DocumentAttachment.pick({
  id: true,
  type: true,
  path: true,
  name: true,
}).safeExtend({
  url: LinkAttachment.shape.url,
});
export type ChatMessageDocumentAttachment = z.infer<
  typeof ChatMessageDocumentAttachment
>;

export const ChatMessageImageAttachment = ImageAttachment.pick({
  id: true,
  type: true,
  path: true,
  name: true,
}).safeExtend({
  url: LinkAttachment.shape.url,
});
export type ChatMessageImageAttachment = z.infer<
  typeof ChatMessageImageAttachment
>;

export const ChatMessageLinkAttachment = LinkAttachment.pick({
  id: true,
  type: true,
  url: true,
});
export type ChatMessageLinkAttachment = z.infer<
  typeof ChatMessageLinkAttachment
>;

export const ChatMessageAttachment = z.discriminatedUnion("type", [
  ChatMessageDocumentAttachment,
  ChatMessageImageAttachment,
  ChatMessageLinkAttachment,
]);
export type ChatMessageAttachment = z.infer<typeof ChatMessageAttachment>;

// ================================================
// CHAT MESSAGE TARGETS
// ================================================

export const ChatMessageTarget = Target.pick({
  target_id: true,
});
export type ChatMessageTarget = z.infer<typeof ChatMessageTarget>;

// ================================================
// CHAT MESSAGES
// ================================================

export const ChatRequestMessage = RequestMessage.pick({
  id: true,
  type: true,
  status: true,
  content: true,
  author: true,
  sequence: true,
  content_type: true,
  created_at: true,
})
  .partial({ created_at: true })
  .extend({
    targets: z.optional(z.array(ChatMessageTarget)),
    // TODO : just make it non-optional
    attachments: z.optional(z.array(ChatMessageAttachment)),
    agentSteps: z.optional(z.array(AgentStepPayload)),
  });
export type ChatRequestMessage = z.infer<typeof ChatRequestMessage>;

const ChatResponseStatus = z.enum([
  ...ResponseMessageStatusEnum.options,
  "limit_reached",
]);

export const ChatResponseGeneratedScreen = z.object({
  answerId: UUID,
  screenId: UUID,
  screenName: z.string(),
  thumbnailUrl: z.url(),
  width: z.number(),
  height: z.number(),
});
export type ChatResponseGeneratedScreen = z.infer<
  typeof ChatResponseGeneratedScreen
>;

export const ChatResponseMessage = ResponseMessage.pick({
  id: true,
  type: true,
  content: true,
  author: true,
  sequence: true,
  created_at: true,
})
  .partial({ created_at: true })
  .extend({
    request_message_id: z.optional(ResponseMessage.shape.request_message_id),
    content_type: z.enum([
      ResponseMessageContentTypeEnum.enum.summary,
      ResponseMessageContentTypeEnum.enum.text,
      ResponseMessageContentTypeEnum.enum.followup,
    ]),
    // TODO : separate targets and generatedScreens - they are mandatory on summary messages
    targets: z.optional(z.array(ChatMessageTarget)),
    generatedScreens: z.optional(z.array(ChatResponseGeneratedScreen)),
    status: ChatResponseStatus,
  });
export type ChatResponseMessage = z.infer<typeof ChatResponseMessage>;

export const ChatMessage = z.discriminatedUnion("type", [
  ChatRequestMessage,
  ChatResponseMessage,
]);
export type ChatMessage = z.infer<typeof ChatMessage>;

export const ChatMessages = z.array(ChatMessage);
export type ChatMessages = z.infer<typeof ChatMessages>;
